import type { LeaveTierDto, LeaveRuleDto } from '../api/hr-admin';

/**
 * 연차정책 편집의 말과 규칙 (2026-08-30 · 판 J).
 *
 * ★★ **고치는 것이 아니라 «새 판» 을 만든다.** 서버가 그렇게 정해 뒀다 — 기존 행은 안 고치고
 *    시행일자별로 쌓이며, 같은 날짜면 409 다. 그래야 **지난 계산이 흔들리지 않는다**
 *    (작년 연차가 올해 규칙으로 다시 계산되면 아무도 설명할 수 없다).
 * ★★ **법정 최소는 바닥이다** — 회사가 더 줄 수는 있어도 덜 줄 수는 없다.
 *    화면이 막지는 않지만(회사 사정을 다 알 수 없다) **아래로 내려가면 말은 해 준다.**
 */

/** 구간 한 줄의 종류 — 세 가지 모양을 사람 말로 가른다. */
export type TierKind = 'MONTHLY' | 'FIXED' | 'STEP';

export const TIER_KIND_LABEL: Record<TierKind, string> = {
  MONTHLY: '달마다 붙는다',
  FIXED: '해마다 며칠',
  STEP: '몇 년마다 늘어난다',
};

export function tierKindOf(t: LeaveTierDto): TierKind {
  if (t.perMonth !== undefined) return 'MONTHLY';
  if (t.everyMonths && t.plus) return 'STEP';
  return 'FIXED';
}

/** 그 줄을 사람 말로 — 목록과 미리보기가 같은 말을 쓴다. */
export function tierText(t: LeaveTierDto, maxDays: number): string {
  const y = Math.floor(t.months / 12);
  switch (tierKindOf(t)) {
    case 'MONTHLY':
      return `${t.months === 0 ? '1년 미만' : `${y}년 미만`} · 달마다 ${t.perMonth}일`
        + (t.max !== undefined ? ` (최대 ${t.max}일)` : '');
    case 'STEP':
      return `${y}년째부터 ${(t.everyMonths ?? 0) / 12}년마다 +${t.plus}일 (최대 ${maxDays}일)`;
    default:
      return `${y}년 이상 · ${t.days ?? 0}일`;
  }
}

/** 법정 최소(근로기준법 제60조)의 뼈대 — **견주기용**이지 강제하지 않는다. */
export const STATUTORY_MIN = { firstYearPerMonth: 1, oneYearDays: 15 } as const;

export interface PolicyWarning { code: string; text: string }

/**
 * 새 판을 보고 **말해 줄 것**을 모은다. 막지 않는다 — 회사 사정을 다 알 수 없다.
 * ★ 「막지 않고 말한다」는 이 코드베이스가 결재 사다리·메뉴 권한에서 쓴 것과 같은 태도다.
 */
export function warningsFor(rule: Pick<LeaveRuleDto, 'tiers' | 'maxDays'>): PolicyWarning[] {
  const out: PolicyWarning[] = [];
  const sorted = [...rule.tiers].sort((a, b) => a.months - b.months);

  const first = sorted.find((t) => t.months === 0 && t.perMonth !== undefined);
  if (first && (first.perMonth ?? 0) < STATUTORY_MIN.firstYearPerMonth) {
    out.push({
      code: 'FIRST_YEAR',
      text: `1년 미만은 **달마다 ${STATUTORY_MIN.firstYearPerMonth}일**이 법정 최소입니다(근로기준법 제60조 ②).`,
    });
  }
  const oneYear = sorted.find((t) => t.months === 12);
  if (oneYear && (oneYear.days ?? 0) < STATUTORY_MIN.oneYearDays) {
    out.push({
      code: 'ONE_YEAR',
      text: `1년 이상은 **${STATUTORY_MIN.oneYearDays}일**이 법정 최소입니다(근로기준법 제60조 ①).`,
    });
  }
  if (rule.maxDays < STATUTORY_MIN.oneYearDays) {
    out.push({ code: 'MAX', text: `상한이 ${STATUTORY_MIN.oneYearDays}일보다 낮으면 법정 최소를 넘지 못합니다.` });
  }
  if (sorted.length === 0) {
    out.push({ code: 'EMPTY', text: '구간이 하나도 없으면 연차가 계산되지 않습니다.' });
  }
  return out;
}

/** 시행일자가 오늘보다 앞이면 **이미 지난 날**이다 — 지난 계산이 바뀔 수 있다. */
export function isBackdated(effectiveFrom: string, today: string): boolean {
  return !!effectiveFrom && effectiveFrom < today;
}
